import type { ApprovalChainRequest, ApprovalChainView, ChainAppliesTo } from '@nest-recruit/shared';

// ADMIN-06 (Admin redesign 2026-11-02, option A): a chain being edited on the page, and the pure rules the page applies to it. Kept
// apart from the page so each rule is tested on its own.

export interface DraftApprover {
  readonly kind: 'user' | 'role';
  readonly id: string | null;
  /** The name as saved ("Any Recruiter", "Kamrul Hasan"), shown when that approver is no longer among the choices. */
  readonly label: string | null;
}

export interface DraftStep {
  readonly name: string;
  readonly approvers: readonly DraftApprover[];
}

export interface DraftChain {
  readonly id: string | null;
  readonly version: number;
  readonly name: string;
  readonly ventureId: string | null;
  readonly departmentId: string | null;
  readonly isActive: boolean;
  readonly appliesTo: ChainAppliesTo;
  /** Offer chains only (S23 Q3): who approves an offer outside the pay band. */
  readonly extraApprover: DraftApprover | null;
  readonly steps: readonly DraftStep[];
}

const EMPTY_APPROVER: DraftApprover = { kind: 'user', id: null, label: null };

/** A new chain's first step. A hire-request chain's steps follow the HR check, so it starts where Leadership was. */
export function newChain(appliesTo: ChainAppliesTo, ventureId: string | null): DraftChain {
  return {
    id: null,
    version: 0,
    name: '',
    ventureId,
    departmentId: null,
    isActive: true,
    appliesTo,
    extraApprover: appliesTo === 'offer' ? EMPTY_APPROVER : null,
    steps: [{ name: appliesTo === 'offer' ? 'Finance head' : 'Final sign-off', approvers: [EMPTY_APPROVER] }],
  };
}

function draftApprover(approver: { readonly approverUserId: string | null; readonly approverRoleId: string | null; readonly label: string }): DraftApprover {
  return approver.approverUserId
    ? { kind: 'user', id: approver.approverUserId, label: approver.label }
    : { kind: 'role', id: approver.approverRoleId, label: approver.label };
}

export function draftFrom(chain: ApprovalChainView): DraftChain {
  return {
    id: chain.id,
    version: chain.version,
    name: chain.name,
    ventureId: chain.ventureId,
    departmentId: chain.departmentId,
    isActive: chain.isActive,
    appliesTo: chain.appliesTo,
    extraApprover: chain.extraApprover ? draftApprover(chain.extraApprover) : chain.appliesTo === 'offer' ? EMPTY_APPROVER : null,
    steps: chain.steps.map((step) => ({ name: step.name, approvers: step.approvers.map(draftApprover) })),
  };
}

function approverBody(approver: DraftApprover): { approverUserId: string | null } | { approverRoleId: string | null } {
  return approver.kind === 'user' ? { approverUserId: approver.id } : { approverRoleId: approver.id };
}

/** What is sent. Grade is gone (29 October); an offer chain has no department (it is chosen on the job). */
export function chainBody(draft: DraftChain): ApprovalChainRequest {
  const isOffer = draft.appliesTo === 'offer';
  return {
    name: draft.name.trim(),
    ventureId: draft.ventureId ?? '',
    departmentId: isOffer ? null : draft.departmentId,
    isActive: draft.isActive,
    appliesTo: draft.appliesTo,
    extraApprover: isOffer && draft.extraApprover ? approverBody(draft.extraApprover) : null,
    steps: draft.steps.map((step) => ({ name: step.name.trim(), approvers: step.approvers.map(approverBody) })),
  };
}

/** Whether two drafts would save the same chain (labels and stray spaces don't count). */
export function isSameChain(a: DraftChain, b: DraftChain): boolean {
  return JSON.stringify(chainBody(a)) === JSON.stringify(chainBody(b));
}

/** What still stops Save, in words; null when it can be saved. The server checks the same and more (who can decide). */
export function whatIsMissing(draft: DraftChain): string | null {
  if (draft.name.trim().length < 2) return 'Name the chain.';
  if (draft.ventureId === null) return 'Choose its venture.';
  if (draft.steps.length === 0) return 'Add a step.';
  const unnamed = draft.steps.findIndex((step) => step.name.trim() === '');
  if (unnamed >= 0) return `Name step ${stepNumberOf(draft.appliesTo, unnamed)}.`;
  const open = draft.steps.findIndex((step) => step.approvers.some((approver) => approver.id === null));
  if (open >= 0) return `Choose every approver in step ${stepNumberOf(draft.appliesTo, open)}.`;
  if (draft.appliesTo === 'offer' && (draft.extraApprover?.id ?? null) === null) return 'Choose who approves an offer outside the pay band.';
  return null;
}

/** The number a person sees on a step: a hire-request chain's steps come after the HR check, which is step 1 (§L41). */
export function stepNumberOf(appliesTo: ChainAppliesTo, index: number): number {
  return appliesTo === 'requisition' ? index + 2 : index + 1;
}

/** §L41: a step whose one approver is the HR role asks what the HR check already asked, so requests skip it. */
export function stepRepeatsHrCheck(step: DraftStep, hrCheckRoleId: string | null): boolean {
  const [only, ...others] = step.approvers;
  return hrCheckRoleId !== null && only !== undefined && others.length === 0 && only.kind === 'role' && only.id === hrCheckRoleId;
}

export function moveStep(draft: DraftChain, index: number, by: -1 | 1): DraftChain {
  const target = index + by;
  if (target < 0 || target >= draft.steps.length) return draft;
  const steps = [...draft.steps];
  const [moved] = steps.splice(index, 1);
  if (moved) steps.splice(target, 0, moved);
  return { ...draft, steps };
}

export function removeStep(draft: DraftChain, index: number): DraftChain {
  return draft.steps.length <= 1 ? draft : { ...draft, steps: draft.steps.filter((_, i) => i !== index) };
}

export function addStep(draft: DraftChain): DraftChain {
  return { ...draft, steps: [...draft.steps, { name: '', approvers: [EMPTY_APPROVER] }] };
}

export function replaceStep(draft: DraftChain, index: number, step: DraftStep): DraftChain {
  return { ...draft, steps: draft.steps.map((current, i) => (i === index ? step : current)) };
}

export function addApprover(step: DraftStep): DraftStep {
  return { ...step, approvers: [...step.approvers, EMPTY_APPROVER] };
}

export function replaceApprover(step: DraftStep, index: number, approver: DraftApprover): DraftStep {
  return { ...step, approvers: step.approvers.map((current, i) => (i === index ? approver : current)) };
}

export function removeApprover(step: DraftStep, index: number): DraftStep {
  return step.approvers.length <= 1 ? step : { ...step, approvers: step.approvers.filter((_, i) => i !== index) };
}

/** "Find a chain": its name, venture or department, ignoring case. */
export function chainMatches(chain: ApprovalChainView, query: string): boolean {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const text = `${chain.name} ${chain.ventureName} ${chain.departmentName ?? 'every department'}`.toLowerCase();
  return words.every((word) => text.includes(word));
}

/** Where a chain applies, as the list says it. An offer chain is chosen on the job, so only its venture matters. */
export function chainScope(chain: Pick<ApprovalChainView, 'appliesTo' | 'ventureName' | 'departmentName'>): string {
  if (chain.appliesTo === 'offer') return `${chain.ventureName} · chosen on each job`;
  return `${chain.ventureName} · ${chain.departmentName ?? 'Every department'}`;
}
